import { BoxxGenericState, getGenericInitialState } from "@boxx/core";
import {
  CategoryModel,
  IStoreCategoriesStateError,
  IStoreCategoriesStateSuccess,
  StoreCategoriesStateType,
} from "@boxx/store-core";
import { ActionReducer, createReducer, on } from "@ngrx/store";
import * as fromCategoriesActions from "./store-categories.actions";

export interface IStoreCategoriesState
  extends BoxxGenericState<
    CategoryModel[],
    IStoreCategoriesStateSuccess,
    IStoreCategoriesStateError
  > {
  selectedCategory: CategoryModel;
}

export const categoriesInitialState: IStoreCategoriesState = {
  ...getGenericInitialState(),
  data: [],
  selectedCategory: null,
};

export const storeCategoriesReducer: ActionReducer<IStoreCategoriesState> =
  createReducer(
    categoriesInitialState,
    on(
      fromCategoriesActions.loadCategoriesBeginAction,
      fromCategoriesActions.deleteCategoryBeginAction,
      (state): IStoreCategoriesState => ({
        ...state,
        isLoading: true,
        error: null,
        success: null,
      })
    ),
    on(
      fromCategoriesActions.loadCategorieByIdBeginAction,
      fromCategoriesActions.loadImageCategoryUploadedBeginAction,
      fromCategoriesActions.createCategoryBeginAction,
      (state): IStoreCategoriesState => ({
        ...state,
        //isLoading: true,
        error: null,
        success: null,
      })
    ),
    on(
      fromCategoriesActions.uploadImageCategoryBeginAction,
      fromCategoriesActions.updateCategoryBeginAction,
      (state): IStoreCategoriesState => ({
        ...state,
        isLoading: false,
        error: null,
        success: null,
      })
    ),
    on(
      fromCategoriesActions.loadCategoriesSuccessAction,
      fromCategoriesActions.deleteCategorySuccessAction,
      (state, action): IStoreCategoriesState => ({
        ...state,
        isLoading: false,
        fetched: true,
        data: action.payload,
        error: null,
        success: {
          after: getStoreCategoriesActionType(action.type),
          data: action.payload,
        },
      })
    ),
    on(
      fromCategoriesActions.createCategorySuccessAction,
      (state, action): IStoreCategoriesState => ({
        ...state,
        //isLoading: false,
        fetched: true,
        data: action.payload,
        error: null,
        success: {
          after: getStoreCategoriesActionType(action.type),
          data: action.payload,
        },
      })
    ),
    on(
      fromCategoriesActions.uploadImageCategorySuccessAction,
      (state, action): IStoreCategoriesState => ({
        ...state,
        //isLoading: false,
        fetched: true,
        error: null,
        success: { after: getStoreCategoriesActionType(action.type) },
      })
    ),
    // eslint-disable-next-line @typescript-eslint/naming-convention
    on(fromCategoriesActions.updateCategorySuccessAction, (state, action) =>
      Object.assign({}, state, {
        //isLoading: false,
        fetched: true,
        error: null,
        success: {
          after: getStoreCategoriesActionType(action.type),
          data: action.payload,
        },
        data: state.data.map((cat) => {
          if (cat.id === action.payload.id) {
            return {
              ...action.payload,
            };
          }
          return cat;
        }),
      })
    ),
    on(
      fromCategoriesActions.loadImageCategoryUploadedSuccessAction,
      (state, action) =>
        Object.assign({}, state, {
          //isLoading: false,
          fetched: true,
          error: null,
          success: {
            after: getStoreCategoriesActionType(action.type),
            data: action.payload,
          },
          data: state.data.map((cat) => {
            if (cat.id === action.payload.id) {
              return {
                ...action.payload,
              };
            }
            return cat;
          }),
        })
    ),

    on(
      fromCategoriesActions.deleteImageCategorySuccessAction,
      (state, action) =>
        Object.assign({}, state, {
          isLoading: false,
          fetched: true,
          error: null,
          success: {
            after: getStoreCategoriesActionType(action.type),
            data: action.payload,
          },
          data: state.data.map((cat) => {
            if (cat.id === action.payload.id) {
              return {
                ...action.payload,
              };
            }
            return cat;
          }),
        })
    ),
    on(
      fromCategoriesActions.loadCategorieByIdSuccessAction,
      (state, action): IStoreCategoriesState => ({
        ...state,
        //isLoading: false,
        fetched: true,
        selectedCategory: action.payload,
        error: null,
        success: { after: getStoreCategoriesActionType(action.type) },
      })
    ),
    on(
      fromCategoriesActions.loadCategoriesFailAction,
      fromCategoriesActions.deleteCategoryFailAction,
      (state, action): IStoreCategoriesState => ({
        ...state,
        isLoading: false,
        error: {
          after: getStoreCategoriesActionType(action.type),
          error: action.errors,
        },
      })
    ),
    on(
      fromCategoriesActions.loadCategorieByIdFailAction,
      fromCategoriesActions.loadImageCategoryUploadedFailAction,
      fromCategoriesActions.createCategoryFailAction,
      fromCategoriesActions.uploadImageCategoryFailAction,
      fromCategoriesActions.updateCategoryFailAction,
      (state, action): IStoreCategoriesState => ({
        ...state,
        //isLoading: false,
        error: {
          after: getStoreCategoriesActionType(action.type),
          error: action.errors,
        },
      })
    ),
    on(
      fromCategoriesActions.clearCategoriesSuccessErrorDataAction,
      (state) => ({
        ...state,
        error: null,
        success: null,
      })
    )
  );

const getStoreCategoriesActionType = (
  type: fromCategoriesActions.StoreCategoriesActionTypes
) => {
  let action: StoreCategoriesStateType;

  switch (type) {
    case fromCategoriesActions.StoreCategoriesActionTypes.loadCategoriesSuccess:
    case fromCategoriesActions.StoreCategoriesActionTypes.loadCategoriesFail:
      action = "LOAD_CATEGORIES";
      break;
    case fromCategoriesActions.StoreCategoriesActionTypes.createCategorySuccess:
    case fromCategoriesActions.StoreCategoriesActionTypes.createCategoryFail:
      action = "CREATE_CATEGORY";
      break;
    case fromCategoriesActions.StoreCategoriesActionTypes.updateCategorySuccess:
    case fromCategoriesActions.StoreCategoriesActionTypes.updateCategoryFail:
      action = "UPDATE_CATEGORY";
      break;
    case fromCategoriesActions.StoreCategoriesActionTypes
      .uploadImageCategorySuccess:
    case fromCategoriesActions.StoreCategoriesActionTypes
      .uploadImageCategoryFail:
      action = "UPLOAD_IMAGE_CATEGORY";
      break;
    case fromCategoriesActions.StoreCategoriesActionTypes.deleteCategorySuccess:
    case fromCategoriesActions.StoreCategoriesActionTypes.deleteCategoryFail:
      action = "DELETE_CATEGORY";
      break;
    case fromCategoriesActions.StoreCategoriesActionTypes
      .loadImageCategoryUploadedSuccess:
    case fromCategoriesActions.StoreCategoriesActionTypes
      .loadImageCategoryUploadedFail:
      action = "CATEGORY_IMAGE_UPLOADED";
      break;
    case fromCategoriesActions.StoreCategoriesActionTypes.loadCategoryByIdSuccess:
    case fromCategoriesActions.StoreCategoriesActionTypes.loadCategoryByIdFail:
      action = "GET_SINGLE_CATEGORY";
      break;
    case fromCategoriesActions.StoreCategoriesActionTypes.deleteImageCategorySuccess:
    case fromCategoriesActions.StoreCategoriesActionTypes.deleteImageCategoryFail:
      action = "DELETE_IMAGE_CATEGORY";
      break;
    default:
      action = "UNKNOWN";
  }

  return action;
};
